import { z, ZodError, ZodIssueCode, type ZodIssue } from 'zod'

const joinPath = (path: (string | number)[]): string => {
  if (path.length === 1) {
    return path[0]?.toString() ?? ''
  }

  return path.reduce<string>((acc, item) => {
    if (typeof item === 'number') {
      return `${acc}[${item}]`
    }

    return `${acc}${acc.length === 0 ? '' : '.'}${item}`
  }, '')
}

export const printZodErrors = (errors: ZodIssue[]) =>
  `Validation Error: ${errors.map((x) => `"${joinPath(x.path)}": ${x.message}`).join('\n')}`

export const urlSchema = z.string().transform((url, ctx) => {
  url = url.trim()
  if (!url.includes('://')) {
    url = `https://${url}`
  }
  try {
    const x = new URL(url)
    if (x.protocol !== 'http:' && x.protocol !== 'https:') {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: 'Invalid protocol, only URLs starting with HTTP and HTTPS are supported',
      })
      return z.NEVER
    }

    if (!/.\.[a-zA-Z]{2,}$/.test(x.hostname)) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: 'Invalid TLD',
      })
      return z.NEVER
    }
    const pathName = x.pathname.endsWith('/') ? x.pathname.slice(0, -1) : x.pathname
    return `${x.origin}${pathName}${x.search ? x.search : ''}`
  } catch (e) {
    ctx.addIssue({
      code: ZodIssueCode.custom,
      message: 'Invalid URL: ' + (e instanceof ZodError ? printZodErrors(e.errors) : e),
    })
    return z.NEVER
  }
})

export const safeParseURLS = (urls: string[]) =>
  urls
    .map((url) => {
      const parsed = urlSchema.safeParse(url)
      return parsed.success && parsed.data
    })
    .filter(Boolean) as string[]
